import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import Keys from '~/constants/Keyboard';

import './index.scss';

const MENU_MARGIN = {
  RIGHT: 100,
  BOTTOM: 90
};

const propTypes = {
  backward: PropTypes.func,
  center: PropTypes.func,
  copy: PropTypes.func,
  crop: PropTypes.func,
  cut: PropTypes.func,
  forward: PropTypes.func,
  hasClipping: PropTypes.bool,
  paste: PropTypes.func,
  position: PropTypes.object,
  remove: PropTypes.func,
  selectAll: PropTypes.func,
  selectedIconIds: PropTypes.arrayOf(PropTypes.string),
  uploadCropEnabled: PropTypes.func,
  onClose: PropTypes.func
};

/** @extends {Component<PropTypes.InferProps<typeof propTypes>>} */
export default class DesignContextMenu extends React.Component {
  static propTypes = propTypes;

  state = {
    offset: null
  };

  UNSAFE_componentWillMount() {
    document.body.addEventListener('mousedown', this.handleMouseDown);
    document.body.addEventListener('keydown', this.closeOnEsc);
  }

  componentDidMount() {
    this.preventOverflow();
  }

  componentWillUnmount() {
    document.body.removeEventListener('mousedown', this.handleMouseDown);
    document.body.removeEventListener('keydown', this.closeOnEsc);
  }

  element = null;

  // Stubbed in tests
  getOffsetParentSize() {
    return {
      width: this.element.offsetParent.clientWidth,
      height: this.element.offsetParent.clientHeight
    };
  }

  // Stubbed in tests
  getElementSize() {
    return {
      width: this.element.clientWidth,
      height: this.element.clientHeight
    };
  }

  handleMouseDown = e => {
    if (!this.element.contains(e.target)) {
      this.props.onClose();
    }
  };

  closeOnEsc = e => {
    if (e.key === Keys.ESCAPE) {
      e.stopPropagation();
      this.props.onClose();
    }
  };

  // Most of the actions are sharing the same routine here
  callAction = action => {
    // Check if any icon is selected ...
    if (!this.props.selectedIconIds.length) {
      return;
    }

    // call the action and close the contextMenu ...
    this.props[action]();
    this.props.onClose();
  };

  selectAll = () => {
    this.props.selectAll();
    this.props.onClose();
  };

  center = () => this.callAction('center');

  forward = () => this.callAction('forward');

  backward = () => this.callAction('backward');

  remove = () => this.callAction('remove');

  copy = () => this.callAction('copy');

  cut = () => this.callAction('cut');

  crop = () => this.callAction('crop');

  paste = () => {
    if (!this.props.hasClipping) {
      return;
    }

    this.props.paste();
    this.props.onClose();
  };

  preventOverflow = () => {
    const position = this.props.position;
    const parentSize = this.getOffsetParentSize();
    const elementSize = this.getElementSize();

    const offset = {
      left: Math.min(
        0,
        parentSize.width -
          (position.left + elementSize.width + MENU_MARGIN.RIGHT)
      ),
      top: Math.min(
        0,
        parentSize.height -
          (position.top + elementSize.height + MENU_MARGIN.BOTTOM)
      )
    };

    this.setState({offset});
  };

  getPosition = () => {
    const {position} = this.props;
    const {offset} = this.state;

    return {
      left: position.left + (offset && offset.left),
      top: position.top + (offset && offset.top)
    };
  };

  render() {
    const hasNoIconSelected = !this.props.selectedIconIds.length;
    const hasNoIconToPaste = !this.props.hasClipping;
    const position = this.getPosition();
    const stopPropagation = e => e.stopPropagation();

    return (
      <div
        ref={node => {
          this.element = node;
        }}
        className="ndx-DesignContextMenu"
        style={position}
        onMouseDown={stopPropagation}>
        <div className="ndx-DesignContextMenu-menu">
          <div
            className={classnames(
              'ndx-DesignContextMenu-option is-option-center',
              {'is-disabled': hasNoIconSelected}
            )}
            onClick={this.center}>
            Center
          </div>

          <div
            className={classnames(
              'ndx-DesignContextMenu-option is-option-forward',
              {'is-disabled': hasNoIconSelected}
            )}
            onClick={this.forward}>
            Bring to front
          </div>

          <div
            className={classnames(
              'ndx-DesignContextMenu-option is-option-backward',
              {'is-disabled': hasNoIconSelected}
            )}
            onClick={this.backward}>
            Send to back
          </div>

          {this.props.uploadCropEnabled() && (
            <>
              <hr className="ndx-DesignContextMenu-separator" />

              <div
                className={classnames(
                  'ndx-DesignContextMenu-option is-option-crop',
                  {'is-disabled': hasNoIconSelected}
                )}
                onClick={this.crop}>
                Crop
              </div>

              <hr className="ndx-DesignContextMenu-separator" />
            </>
          )}
          <div
            className={classnames(
              'ndx-DesignContextMenu-option is-option-selectAll'
            )}
            onClick={this.selectAll}>
            Select all
          </div>

          <hr className="ndx-DesignContextMenu-separator" />

          <div
            className={classnames(
              'ndx-DesignContextMenu-option is-option-cut',
              {'is-disabled': hasNoIconSelected}
            )}
            onClick={this.cut}>
            Cut
          </div>

          <div
            className={classnames(
              'ndx-DesignContextMenu-option is-option-copy',
              {'is-disabled': hasNoIconSelected}
            )}
            onClick={this.copy}>
            Copy
          </div>

          <div
            className={classnames(
              'ndx-DesignContextMenu-option is-option-paste',
              {'is-disabled': hasNoIconToPaste}
            )}
            onClick={this.paste}>
            Paste
          </div>

          <hr className="ndx-DesignContextMenu-separator" />

          <div
            className={classnames(
              'ndx-DesignContextMenu-option is-option-remove',
              {'is-disabled': hasNoIconSelected}
            )}
            onClick={this.remove}>
            Delete
          </div>
        </div>
      </div>
    );
  }
}
